import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  type FilterValues,
  readCommittedFilters,
  writeFilters,
} from '@/utils/filterParams'

export type FormValues = FilterValues

export type FormContextType = {
  values: FormValues
  setValues: (next: FormValues | ((prev: FormValues) => FormValues)) => void
  apply: () => void
  cancel: () => void
}

/**
 * `null` default = "no staging provider mounted" → `useFilterValues` falls back
 * to live URL read/write. Branch on `null` rather than throwing — the inline
 * case legitimately has no provider.
 */
export const FormContext = createContext<FormContextType | null>(null)

/**
 * STAGING provider — for the dropdown panel and mobile sheet only (mount-on-open).
 *
 * Holds local state seeded from `initialValues` (the current URL filters, read
 * on open). Edits stay in memory until `apply` flushes them via the injected
 * `commit`; `cancel` resets to the seed. Because it mounts fresh each open it
 * always re-seeds from the URL — no stale-mirror window.
 *
 * `initialRef` is captured ONCE at mount and never reassigned: `cancel` must
 * revert to what was committed when the panel opened, not to a later snapshot.
 */
export function FormContextProvider({
  initialValues = {},
  commit,
  children,
}: {
  initialValues?: FormValues
  commit: (values: FormValues) => void
  children: React.ReactNode
}) {
  const [values, setValuesState] = useState<FormValues>(initialValues)

  // Refs keep apply/cancel stable ([] deps) while reading latest state/commit.
  const valuesRef = useRef(values)
  const commitRef = useRef(commit)
  useLayoutEffect(() => {
    valuesRef.current = values
    commitRef.current = commit
  })

  // Captured once at mount — never updated after.
  const initialRef = useRef(initialValues)

  const setValues = useCallback<FormContextType['setValues']>((next) => {
    setValuesState((prev) => (typeof next === 'function' ? next(prev) : next))
  }, [])

  const apply = useCallback(() => {
    commitRef.current(valuesRef.current)
  }, [])

  const cancel = useCallback(() => {
    setValuesState(initialRef.current)
  }, [])

  const value = useMemo<FormContextType>(
    () => ({ values, setValues, apply, cancel }),
    [values, setValues, apply, cancel],
  )

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>
}

/**
 * Uniform filter-values hook used by all filter components.
 *
 * - **Inside a `FormContextProvider`** (staged — dropdown / mobile sheet):
 *   returns the provider's `{ values, setValues }`. Edits are staged until
 *   the user hits Apply.
 *
 * - **Without a provider** (inline — big desktop / tablet): reads committed
 *   filters from the URL directly and writes via `writeFilters` on change.
 *   No local state — a URL change from any source is reflected on the next
 *   render (N1 guarantee).
 *
 * `useContext` is always called (no conditional-hook violation); only the
 * return value branches.
 */
export function useFilterValues(): Pick<
  FormContextType,
  'values' | 'setValues'
> {
  const ctx = useContext(FormContext)

  if (ctx) {
    return { values: ctx.values, setValues: ctx.setValues }
  }

  // Inline fallback — live URL read, immediate write.
  const values = readCommittedFilters()
  const setValues: FormContextType['setValues'] = (next) => {
    const resolved =
      typeof next === 'function' ? next(readCommittedFilters()) : next
    writeFilters(resolved)
  }
  return { values, setValues }
}
